// @ts-nocheck
import { type FC, useCallback } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import { Modal, SearchableCard } from "@snapchat/snap-design-system";
import { AbsoluteRoute } from "../../../../core/routes";

import { confirmRemovalModalStyle, searchItemsContainerV2 } from "../../../common/styles";
import { NoResults } from "../../components";
import { type LensUpdateData } from "../models";
import { useModal } from "../../../core/hooks";
import { Maybe } from "../../../core/models";
import { DashboardSpinner } from "../../../dashboard/components";
import { LensInGroupModel } from "../../model";

import { GroupLensesTable } from "./group-lenses-table/GroupLensesTable";

const noSearchResultsMessage = "Your search criteria did not match any Lenses.";
const noLensesAvailableMessage = "This Lens Group is empty.";

interface GroupLensesListProps {
    loading: boolean;
    groupId: string;
    organizationId: string;
    searchValue: Maybe<string | null>;
    lensesInGroup: Readonly<LensInGroupModel[]>;
    readonly: boolean;
    onRemove: (lensId: string) => void;
    onSearch: (lensName: string) => void;
    onLensInGroupChanged: (updatedLens: LensUpdateData) => void;
}

export const GroupLensesList: FC<GroupLensesListProps> = ({
    lensesInGroup,
    loading,
    onRemove,
    groupId,
    organizationId,
    onSearch,
    searchValue,
    onLensInGroupChanged,
    readonly,
}) => {
    const [confirmRemoveModalProps, showConfirmRemoveModal, lensId] = useModal<string>(false);
    const navigate = useNavigate();

    const noResultsMessage = searchValue.ifEmpty(
        () => noSearchResultsMessage,
        () => noLensesAvailableMessage,
    );

    const removeConfirmed = useCallback(() => {
        confirmRemoveModalProps.onCancel();
        onRemove(lensId ?? "");
    }, [confirmRemoveModalProps, lensId, onRemove]);

    const onLensClick = useCallback(
        (lens: LensInGroupModel) => {
            const { removed: isLensRemoved, compoundId } = lens.lens ?? {};

            if (!isLensRemoved && lens.lens && lens.lens.compoundId) {
                const { lensSourceType, id, reference } = lens.lens.compoundId;
                navigate(
                    generatePath(AbsoluteRoute.ORG_LENS_SCHEDULER_GROUP_LENS, {
                        orgId: organizationId,
                        groupId: groupId,
                        lensId: id ?? "",
                        reference: reference ?? "",
                        lensSourceType: lensSourceType ?? "",
                    }),
                );
            }
        },
        [organizationId, groupId, navigate],
    );

    if (loading) {
        return <DashboardSpinner />;
    }

    const isNoLenses = (lensesInGroup?.length ?? 0) === 0;
    const showV2Table = !isNoLenses;

    return (
        <>
            <SearchableCard
                className={searchItemsContainerV2}
                padBody={false}
                searchPlaceholder="Search Lenses in this Group"
                onSearch={onSearch}
                searchValue={searchValue.getOrDefault("")}
            >
                {isNoLenses && <NoResults description={noResultsMessage} />}
                {showV2Table && (
                    <GroupLensesTable
                        lenses={lensesInGroup}
                        readonly={readonly}
                        onLensRemove={showConfirmRemoveModal}
                        onLensChange={onLensInGroupChanged}
                        onLensClick={onLensClick}
                    />
                )}
            </SearchableCard>
            <Modal
                className={confirmRemovalModalStyle}
                title="Remove Lens from Group"
                {...confirmRemoveModalProps}
                onAction={removeConfirmed}
                actionText={<span data-testid="remove-lens-confirm-dialog-remove-button">Remove</span>}
            >
                <span>Are you sure you want to remove this Lens from the Lens Group?</span>
            </Modal>
        </>
    );
};
